import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

const DOC_TYPE_OPTIONS = [
  { value: 'service_agreement', label: 'Service Agreement' },
  { value: 'property_info', label: 'Property Info Sheet' },
  { value: 'other', label: 'Other' },
];

export default function NewAccountFormPage() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    property_name: '', address: '', city: '', state: 'TX', zip: '',
    contact_name: '', contact_phone: '', contact_email: '', notes: '',
  });

  const [files, setFiles] = useState([]); // { file, docType }

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function addFiles(newFiles) {
    const added = Array.from(newFiles).map(f => ({ file: f, docType: 'service_agreement' }));
    setFiles(prev => [...prev, ...added]);
    if (fileRef.current) fileRef.current.value = '';
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function setDocType(idx, type) {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, docType: type } : f));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.property_name.trim()) { setError('Property name is required'); return; }
    setError('');
    setSubmitting(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));

      // Add files and doc types
      const docTypes = [];
      files.forEach(({ file, docType }) => {
        fd.append('files', file);
        docTypes.push(docType);
      });
      fd.append('doc_types', JSON.stringify(docTypes));

      const account = await api.createAccount(fd);
      navigate(`/sales/accounts/${account.id}`);
    } catch (err) {
      setError(err.message || 'Failed to submit');
    }
    setSubmitting(false);
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Submit New Account</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/sales/accounts')}>Cancel</button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="new-account-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Left: Property Info */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Property Information</h3>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Property Name *</label>
              <input className="sales-input" value={form.property_name} onChange={e => set('property_name', e.target.value)} placeholder="e.g. Sunset Apartments" required />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Address</label>
              <input className="sales-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Street address" />
            </div>

            <div className="new-account-csz" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>City</label>
                <input className="sales-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>State</label>
                <input className="sales-input" value={form.state} onChange={e => set('state', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>ZIP</label>
                <input className="sales-input" value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="ZIP" />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16, marginTop: 16 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Contact</h3>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Contact Name</label>
                <input className="sales-input" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Property manager name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phone</label>
                  <input className="sales-input" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="(555) 555-5555" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Email</label>
                  <input className="sales-input" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="email@example.com" />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
              <textarea className="sales-input" style={{ minHeight: 80, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes..." />
            </div>
          </div>

          {/* Right: Documents */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Documents</h3>

            <div
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e2e8f0'; addFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed #e2e8f0', borderRadius: 12, padding: 32,
                textAlign: 'center', cursor: 'pointer', marginBottom: 16,
                transition: 'border-color 0.2s',
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>&#128206;</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Drop files here or click to upload</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>PDF, images up to 15MB each (max 10 files)</div>
              <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)} />
            </div>

            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {files.map((f, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc',
                  }}>
                    <span style={{ fontSize: 18 }}>
                      {f.file.type?.includes('pdf') ? '📄' : '🖼️'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.file.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{(f.file.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <select value={f.docType} onChange={e => setDocType(idx, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12 }}>
                      {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button type="button" onClick={() => removeFile(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#dc2626', padding: 4 }}>
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>
                No documents attached yet. Upload service agreements and property info sheets.
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/sales/accounts')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit New Account'}
          </button>
        </div>
      </form>
    </div>
  );
}
